<template>
    <v-dialog
        :model-value="isOpen"
        max-width="500px"
        transition="slide-y-transition"
        eager
        @keydown.esc="onClose"
        @click:outside="onClose"
        @after-leave="onAfterLeave"
    >
        <v-card>
            <v-card-title>
                <span class="text-subtitle-1">{{ t('orders.internalOrders.assignProductTemplateDialog.title') }}</span>
            </v-card-title>
            <v-card-text class="text-center">
                <v-autocomplete
                    v-model="productTemplateId"
                    :items="templateItems"
                    :label="t('orders.internalOrders.assignProductTemplateDialog.productTemplate')"
                    item-value="id"
                    item-title="name"
                    variant="outlined"
                    hide-details
                />
            </v-card-text>
            <v-divider />
            <v-card-actions>
                <v-btn color="error" variant="text" data-sentry-label="Anuluj" @click="onClose">
                    {{ t('orders.internalOrders.assignProductTemplateDialog.cancel') }}
                </v-btn>
                <v-spacer />
                <v-btn color="success" variant="text" data-sentry-label="Zapisz" @click="saveProductTemplate">
                    {{ t('orders.internalOrders.assignProductTemplateDialog.save') }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script setup lang="ts">
/*
Organization / ordering applied:
1. Imports
2. Props / Emits
3. Constants
4. Stores & top-level services
5. Reactive state (refs/reactive)
6. Validation rules & setup
7. Computed
8. Handlers / methods
9. Helpers / lifecycle
*/

// 1. Imports
import { computed, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'
import { useProductTemplatesStore } from '@/stores/orders/productTemplates'

// 2. Props / Emits
const props = withDefaults(defineProps<{ isOpen?: boolean }>(), { isOpen: false })
const emit = defineEmits<{
    'on-save': [payload: { productTemplateId: number | null }]
    'on-close': []
}>()

// 3. Constants
// (none)

// 4. Stores & top-level services
const { t } = useI18n()
const productTemplatesStore = useProductTemplatesStore()
const { productTemplates } = storeToRefs(productTemplatesStore)

type ProductTemplateListItem = { id: number; name: string }

const templateItems = computed((): ProductTemplateListItem[] => (productTemplates.value ?? []) as ProductTemplateListItem[])

// 5. Reactive state
const productTemplateId = ref<number | null>(null)

// 6. Validation rules & setup
// (none)

// 7. Computed
// (none)

// 8. Handlers / methods
function saveProductTemplate(): void {
    emit('on-save', { productTemplateId: productTemplateId.value ?? null })
}

function onClose(): void {
    emit('on-close')
}

function onAfterLeave(): void {
    productTemplateId.value = null
}

// 9. Helpers / lifecycle
watch(
    () => props.isOpen,
    (open) => {
        if (open) {
            productTemplateId.value = null
        }
    }
)
</script>
